import { useState } from "react";
import { useDispatch } from "react-redux";

import axios from "../../../../../utils/axios";

import Field from "../../Components/Field";
import Tooltip from "../../../../../Components/common/Tooltip/Tooltip";
import Spinner from "../../../../../Components/common/Spinner/Spinner";

import { addNotification } from "../../../../../store/actions/notifierActions";

import "./styles.scss";

const LdapTest = () => {
  const dispatch = useDispatch();
  const [testCredentials, setTestCredentials] = useState({
    username: "",
    password: "",
  });
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const inputChangeHandler = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setTestCredentials((prev) => ({ ...prev, [name]: value }));
  };

  const testConnection = async () => {
    if (!testCredentials.username.trim() || !testCredentials.password.trim()) {
      dispatch(
        addNotification({
          message: "Please enter both username and password",
          options: { variant: "error" },
          time: 3000,
        }),
      );
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      const response = await axios.post("/api/settings/ldap-test", testCredentials);

      if (response.data.success) {
        setTestResult({
          success: true,
          message: `Authentication successful! Welcome ${response.data.username || testCredentials.username}`,
          userInfo: response.data.user_info || {},
          attempts: response.data.attempts || [],
        });
        dispatch(
          addNotification({
            message: `LDAP test successful! Welcome ${response.data.username || testCredentials.username}`,
            options: { variant: "success" },
            time: 5000,
          }),
        );
      } else {
        setTestResult({
          success: false,
          message: response.data.error || "Authentication failed",
          attempts: response.data.attempts || [],
        });
        dispatch(
          addNotification({
            message: `LDAP test failed: ${response.data.error}`,
            options: { variant: "error" },
            time: 5000,
          }),
        );
      }
    } catch (error) {
      const data = error.response?.data;
      const errorMessage =
        data?.error ||
        data?.message ||
        data?.msg ||
        (error.response?.status
          ? `Request failed (HTTP ${error.response.status}). This is not an LDAP error — check that you are signed in as an admin.`
          : "Connection failed. Please check your LDAP configuration.");
      setTestResult({
        success: false,
        message: errorMessage,
        attempts: error.response?.data?.attempts || [],
      });
      dispatch(
        addNotification({
          message: `LDAP test failed: ${errorMessage}`,
          options: { variant: "error" },
          time: 5000,
        }),
      );
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="ldap-test">
      <div className="ldap-test__header">
        <h3>Test LDAP Authentication</h3>
        <p>Test your LDAP configuration with user credentials.</p>
      </div>
      <div className="ldap-test__content">
        <div className="ldap-test__line">
          <Field
            label="Username"
            tooltipText="Enter the LDAP username to test authentication (e.g., einstein)"
            width="200px"
            name="username"
            value={testCredentials.username}
            onChange={inputChangeHandler}
            placeholder="einstein"
          />
        </div>
        <div className="ldap-test__line">
          <Field
            label="Password"
            tooltipText="Enter the LDAP password to test authentication (e.g., password)"
            width="200px"
            name="password"
            type="password"
            value={testCredentials.password}
            onChange={inputChangeHandler}
            placeholder="password"
          />
        </div>

        {/* Test Result Display */}
        {testResult && (
          <div className="ldap-test__result">
            <div
              style={{
                padding: "12px 16px",
                borderRadius: "6px",
                backgroundColor: testResult.success ? "#d4edda" : "#f8d7da",
                color: testResult.success ? "#155724" : "#721c24",
                border: `1px solid ${testResult.success ? "#c3e6cb" : "#f5c6cb"}`,
                marginBottom: "16px",
              }}>
              <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                {testResult.success ? "✅ Test Successful" : "❌ Test Failed"}
              </div>
              <div style={{ fontSize: "13px" }}>{testResult.message}</div>
              {testResult.success && testResult.userInfo && (
                <div style={{ marginTop: "8px", fontSize: "12px", opacity: "0.8" }}>
                  <div>Username: {testResult.userInfo.username}</div>
                  <div>Email: {testResult.userInfo.email}</div>
                  <div>Full Name: {testResult.userInfo.full_name}</div>
                  {testResult.userInfo.dn && <div>DN: {testResult.userInfo.dn}</div>}
                </div>
              )}
              {testResult.attempts?.length > 0 && (
                <details style={{ marginTop: "10px", fontSize: "12px" }}>
                  <summary style={{ cursor: "pointer" }}>Connection details</summary>
                  <ul style={{ margin: "6px 0 0", paddingLeft: "18px", lineHeight: "1.6" }}>
                    {testResult.attempts.map((step, index) => (
                      <li key={index} style={{ wordBreak: "break-all" }}>
                        {step}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          </div>
        )}

        <button
          className="primary-button"
          onClick={testConnection}
          disabled={
            isTesting || !testCredentials.username.trim() || !testCredentials.password.trim()
          }>
          {isTesting ? <Spinner width={12} height={12} /> : "Test LDAP Connection"}
        </button>
      </div>
    </div>
  );
};

export default LdapTest;
